import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, UserRoundPen } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

// Modifica anagrafica/credenziali di un discente dal backoffice admin
// (PATCH /admin/learners/{id}). La password si cambia solo se compilata.
export default function LearnerEditDialog({ learner, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const open = !!learner;

  useEffect(() => {
    if (learner) {
      setForm({
        first_name: learner.first_name || "",
        last_name: learner.last_name || "",
        fiscal_code: learner.fiscal_code || "",
        email: learner.email || "",
        birth_date: learner.birth_date || "",
        password: "",
      });
    }
  }, [learner]);

  if (!learner || !form) return null;

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const cf = form.fiscal_code.trim().toUpperCase();
    if (!/^[A-Z0-9]{16}$/.test(cf)) {
      toast.error("Il codice fiscale deve essere di 16 caratteri.");
      return;
    }
    if (form.password && form.password.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        first_name: form.first_name,
        last_name: form.last_name,
        fiscal_code: cf,
        email: form.email.trim() || null,
        birth_date: form.birth_date || null,
      };
      if (form.password) body.password = form.password;
      await api.patch(`/admin/learners/${learner.id}`, body);
      toast.success("Discente aggiornato.");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-brand-blue flex items-center gap-2"><UserRoundPen size={18} /> Modifica discente</DialogTitle>
          <DialogDescription>Anagrafica e credenziali. Lascia vuota la password per non cambiarla.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Nome" value={form.first_name} onChange={setF("first_name")} required />
            <Fld label="Cognome" value={form.last_name} onChange={setF("last_name")} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld
              label="Codice fiscale (username)"
              value={form.fiscal_code}
              onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
              required maxLength={16}
            />
            <Fld label="Nuova password (opzionale)" type="text" value={form.password} onChange={setF("password")} placeholder="Lascia vuoto per non cambiarla" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Email (facoltativa)" type="email" value={form.email} onChange={setF("email")} />
            <Fld label="Data di nascita (facoltativa)" type="date" value={form.birth_date} onChange={setF("birth_date")} />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink">Annulla</button>
            <Button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-brand-ink">{label}</Label>
      <Input {...props} className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue" />
    </div>
  );
}
